import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  MarkerType,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type NodeTypes,
  type Connection,
  type Edge,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { v4 as uuidv4 } from "uuid";
import { Goal } from "../../lib/types";
import GoalGraphNode, { type GoalGraphNodeData } from "./GoalGraphNode";
import {
  goalsGraphProfilesDB,
  type GoalsGraphProfile,
} from "../../services/database/goalsGraphProfilesDB";

type GoalsGraphNode = Node<GoalGraphNodeData, "goal">;

const DEFAULT_PROFILE_NAME = "Default";

const buildDefaultNodes = (goals: Goal[]): GoalsGraphNode[] => {
  const colCount = Math.max(1, Math.ceil(Math.sqrt(goals.length)));
  const spacingX = 260;
  const spacingY = 160;
  return goals.map((g, idx) => {
    const row = Math.floor(idx / colCount);
    const col = idx % colCount;
    return {
      id: g.id,
      type: "goal",
      position: { x: col * spacingX, y: row * spacingY },
      data: { goal: g },
    };
  });
};

const ensureGraphShape = (params: {
  goals: Goal[];
  nodes: GoalsGraphNode[];
  edges: Edge[];
}): { nodes: GoalsGraphNode[]; edges: Edge[] } => {
  const goalIds = new Set(params.goals.map((g) => g.id));

  const nodesById = new Map(params.nodes.map((n) => [n.id, n]));
  const nextNodes: GoalsGraphNode[] = [];
  for (const goal of params.goals) {
    const existing = nodesById.get(goal.id);
    if (existing) {
      nextNodes.push({
        ...existing,
        type: "goal",
        data: { goal },
      });
    } else {
      nextNodes.push({
        id: goal.id,
        type: "goal",
        position: { x: 0, y: 0 },
        data: { goal },
      });
    }
  }

  const nextEdges = params.edges.filter(
    (e) => goalIds.has(e.source) && goalIds.has(e.target)
  );

  return { nodes: nextNodes, edges: nextEdges };
};

const nodeTypes: NodeTypes = { goal: GoalGraphNode };

const GoalsGraph: React.FC<{
  goals: Goal[];
  onEditGoal?: (goal: Goal) => void;
}> = ({ goals, onEditGoal }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<GoalsGraphProfile | null>(null);

  const [nodes, setNodes] = useState<GoalsGraphNode[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);

  const hydratedRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);
  const nodesRef = useRef<GoalsGraphNode[]>([]);
  const edgesRef = useRef<Edge[]>([]);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  const visibleGoalIds = useMemo(
    () => new Set(goals.map((g) => g.id)),
    [goals]
  );

  const displayedNodes = useMemo(
    () => nodes.filter((n) => visibleGoalIds.has(n.id)),
    [nodes, visibleGoalIds]
  );
  const displayedEdges = useMemo(
    () =>
      edges.filter(
        (e) => visibleGoalIds.has(e.source) && visibleGoalIds.has(e.target)
      ),
    [edges, visibleGoalIds]
  );

  // Load or create the default profile (persisted in Supabase).
  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      try {
        const profiles = await goalsGraphProfilesDB.getAll();
        const existing =
          profiles.find(
            (p) => p.name.toLowerCase() === DEFAULT_PROFILE_NAME.toLowerCase()
          ) || null;

        if (!active) return;

        if (existing) {
          setProfile(existing);
          const next = ensureGraphShape({
            goals,
            nodes: existing.nodes as any,
            edges: existing.edges as any,
          });
          setNodes(next.nodes);
          setEdges(
            next.edges.map((e) => ({
              ...e,
              markerEnd: { type: MarkerType.ArrowClosed },
            }))
          );
        } else {
          const nextNodes = buildDefaultNodes(goals);
          const created: GoalsGraphProfile = {
            id: uuidv4(),
            name: DEFAULT_PROFILE_NAME,
            nodes: nextNodes,
            edges: [],
            createdAt: Date.now(),
          };
          const saved = await goalsGraphProfilesDB.upsert(created);
          if (!active) return;
          setProfile(saved || created);
          setNodes(nextNodes);
          setEdges([]);
        }
      } catch (error) {
        console.error("[GoalsGraph] failed to load profile", error);
        if (!active) return;
        setProfile(null);
        setNodes(buildDefaultNodes(goals));
        setEdges([]);
      } finally {
        if (!active) return;
        hydratedRef.current = true;
        setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep graph in sync when the goals list changes.
  useEffect(() => {
    if (!hydratedRef.current) return;
    const next = ensureGraphShape({ goals, nodes, edges });
    // Only update if something actually changed.
    const nodeIds = new Set(nodes.map((n) => n.id));
    const nextNodeIds = new Set(next.nodes.map((n) => n.id));
    const nodesChanged =
      nodeIds.size !== nextNodeIds.size ||
      Array.from(nextNodeIds).some((id) => !nodeIds.has(id));
    const edgesChanged = next.edges.length !== edges.length;
    if (nodesChanged) setNodes(next.nodes);
    if (edgesChanged) setEdges(next.edges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals]);

  const scheduleSave = useCallback(
    (nextNodes: Node[], nextEdges: Edge[]) => {
      if (!hydratedRef.current) return;
      if (!profile) return;
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = window.setTimeout(async () => {
        const payload: GoalsGraphProfile = {
          ...profile,
          nodes: nextNodes,
          edges: nextEdges,
        };
        const saved = await goalsGraphProfilesDB.upsert(payload);
        if (saved) setProfile(saved);
      }, 600);
    },
    [profile]
  );

  const onNodesChange = useCallback((changes: any) => {
    setNodes((current) => applyNodeChanges(changes, current));
  }, []);

  const onEdgesChange = useCallback(
    (changes: any) => {
      setEdges((current) => {
        const next = applyEdgeChanges(changes, current);
        scheduleSave(nodesRef.current, next);
        return next;
      });
    },
    [scheduleSave]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) => {
        const next = addEdge(
          {
            ...connection,
            markerEnd: { type: MarkerType.ArrowClosed },
          },
          eds
        );
        scheduleSave(nodesRef.current, next);
        return next;
      });
    },
    [scheduleSave]
  );

  const onNodeDragStop = useCallback(() => {
    scheduleSave(nodesRef.current, edgesRef.current);
  }, [scheduleSave]);

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Drag goals to arrange. Drag a connection from one goal to another to
          create a directional pathway. Select and press Delete to remove.
        </Typography>
      </Box>

      <Box
        sx={{
          height: "70vh",
          borderRadius: 2,
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? "#121212" : "#f5f5f5",
        }}
      >
        <ReactFlow
          nodes={displayedNodes}
          edges={displayedEdges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDragStop={onNodeDragStop}
          onNodeClick={(_, node) => {
            const goal = goals.find((g) => g.id === node.id);
            if (goal) onEditGoal?.(goal);
          }}
          deleteKeyCode={["Backspace", "Delete"]}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }}
          style={{ backgroundColor: isDark ? "#121212" : "#f5f5f5" }}
        >
          <Controls />
          <MiniMap
            nodeColor={() => (isDark ? "#5dc4b2" : "#2bb39d")}
            maskColor={isDark ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.8)"}
          />
          <Background
            variant={BackgroundVariant.Dots}
            gap={20}
            size={1}
            color={isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}
          />
        </ReactFlow>
      </Box>
    </Box>
  );
};

export default GoalsGraph;
